import React, { useEffect, useMemo, useState, useCallback } from 'react';
import clsx from 'clsx';
import { useOutletContext } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar } from 'recharts';
import Icon from '../components/Icon';
import { useAuth } from '../contexts/AuthContext';
import { usePagePermissions } from '../hooks/usePagePermissions';
import {
  fetchVendorsSummary,
  fetchVendorTransactions,
  fetchVendorTimeseries,
  fetchVendorIssues,
  refreshVendorTransactions,
} from '../services/api';
import { chartPalette, tooltipContentStyle } from '../utils/chartTheme';
import { timeAgo, formatCurrency } from '../utils/format';

const STATUS_BADGE = {
  operational:  { class: 'badge-success', label: 'Operational' },
  degraded:     { class: 'badge-warning', label: 'Degraded'    },
  down:         { class: 'badge-danger',  label: 'Down'        },
  idle:         { class: 'badge-muted',   label: 'Idle (24h)'  },
  unconfigured: { class: 'badge-muted',   label: 'Unconfigured' },
};

const SEVERITY_BADGE = {
  critical: 'badge-danger',
  high:     'badge-warning',
  medium:   'badge-info',
  low:      'badge-muted',
};

const Vendors = () => {
  const { environment } = useOutletContext();
  const { loading: authLoading } = useAuth();
  const { isSuperAdmin } = usePagePermissions();
  // Vendors monitoring is super-admin gated. Recognise both 'super_admin'
  // (current canonical role) and the legacy 'admin' value via the hook.
  const isAdmin = isSuperAdmin;

  // All hooks must be unconditional — never short-circuit before this block.
  const [summary, setSummary] = useState(null);
  const [issues, setIssues] = useState({ counts: {}, issues: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeVendor, setActiveVendor] = useState('paytrie');
  const [activeTab, setActiveTab] = useState('overview'); // overview | transactions | issues
  const [vendorTxns, setVendorTxns] = useState({ data: [], shape: 'transactions', count: 0, sla_minutes: null });
  const [vendorBuckets, setVendorBuckets] = useState([]);
  const [vendorLoading, setVendorLoading] = useState(true);
  // Sandbox Console — manual refresh against the provider's REST API.
  // Only YC + Paytrie support this today; Sumsub uses a different flow.
  const [sandbox, setSandbox] = useState(null);    // { vendor, environment, items, fetched_at, latency_ms, count }
  const [sandboxLoading, setSandboxLoading] = useState(false);
  const [sandboxError, setSandboxError] = useState(null);

  const refresh = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const [s, i] = await Promise.all([fetchVendorsSummary(environment), fetchVendorIssues(environment, 24)]);
      setSummary(s);
      setIssues(i);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load vendor health');
    } finally {
      setLoading(false);
    }
  }, [environment, isAdmin]);

  const refreshVendor = useCallback(async () => {
    if (!isAdmin || !activeVendor) return;
    setVendorLoading(true);
    try {
      const [t, ts] = await Promise.all([
        fetchVendorTransactions(activeVendor, environment, 24),
        fetchVendorTimeseries(activeVendor, environment, 24),
      ]);
      setVendorTxns(t);
      setVendorBuckets(ts.buckets || []);
    } catch (err) {
      setError(err.message || 'Failed to load vendor detail');
    } finally {
      setVendorLoading(false);
    }
  }, [environment, activeVendor, isAdmin]);

  useEffect(() => {
    refresh();
    const i = setInterval(refresh, 30000);
    return () => clearInterval(i);
  }, [refresh]);

  useEffect(() => { refreshVendor(); }, [refreshVendor]);

  // Auth gate after hooks are declared.
  if (authLoading) return null;
  if (!isAdmin) {
    return (
      <>
        <div className="page-head"><div><h1>Vendors</h1></div></div>
        <div className="card">
          <div className="empty">
            <div className="empty-icon"><Icon name="shield" size={26} /></div>
            <h3>Admin-only page</h3>
            <p>Vendor monitoring is restricted to super-admins. Contact stanford@coynitt.ca for access.</p>
          </div>
        </div>
      </>
    );
  }

  const vendors = summary?.vendors || [];
  const activeVendorRow = vendors.find((v) => v.id === activeVendor);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Vendors</h1>
          <p>
            On-ramp, off-ramp, and KYC partner monitoring. SLA breaches, error rates, contract-decision data
            <span className="text-tx3"> · {environment}</span>
          </p>
        </div>
        <button type="button" className="btn btn-ghost" onClick={() => { refresh(); refreshVendor(); }}>
          Refresh
        </button>
      </div>

      {error && (
        <div role="alert" style={{ background: 'var(--err-bg)', color: 'var(--err)', padding: 'var(--s3)', borderRadius: 'var(--r-md)', fontSize: 13, marginBottom: 'var(--s3)' }}>
          {error}
        </div>
      )}

      {/* Top stats */}
      <div className="dashboard-grid stagger mb-4">
        {loading && vendors.length === 0
          ? [0, 1, 2, 3].map((i) => <div key={i} className="skeleton skeleton-block" style={{ height: 110 }} />)
          : vendors.map((v) => (
              <button
                type="button"
                key={v.id}
                onClick={() => setActiveVendor(v.id)}
                className="card"
                style={{
                  textAlign: 'left',
                  cursor: 'pointer',
                  borderColor: activeVendor === v.id ? 'var(--teal)' : 'var(--bd)',
                  borderWidth: activeVendor === v.id ? 2 : 1,
                  background: 'var(--bg-card)',
                }}
              >
                <div className="row mb-2" style={{ justifyContent: 'space-between' }}>
                  <div className="col col-gap-2">
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--tx1)' }}>{v.name}</div>
                    <div className="text-tx3 text-xs">{v.role_label}</div>
                  </div>
                  <span className={clsx('badge', STATUS_BADGE[v.status]?.class || 'badge-muted')}>
                    {STATUS_BADGE[v.status]?.label || v.status}
                  </span>
                </div>
                <div className="row row-gap-3" style={{ alignItems: 'flex-end' }}>
                  <div className="col col-gap-2 flex-1">
                    <div className="text-tx3 text-xs">Volume 24h</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--tx1)' }}>
                      {formatCurrency(v.transactions_24h.volume_cad, { compact: true })}
                    </div>
                  </div>
                  <div className="col col-gap-2 flex-1">
                    <div className="text-tx3 text-xs">Txns / SLA</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: v.sla_breaches > 0 ? 'var(--err)' : 'var(--tx1)' }}>
                      {v.transactions_24h.count}
                      {v.sla_breaches > 0 && <span className="text-tx3 text-xs"> · {v.sla_breaches} breach</span>}
                    </div>
                  </div>
                </div>
              </button>
            ))}
      </div>

      {/* Issues panel — always visible across all vendors */}
      {issues.issues.length > 0 && (
        <div className="card mb-4" style={{ borderLeft: `3px solid ${issues.counts.critical > 0 ? 'var(--err)' : 'var(--warn)'}` }}>
          <div className="row mb-3" style={{ justifyContent: 'space-between' }}>
            <div className="card-title" style={{ margin: 0 }}>
              Active Issues · {issues.counts.total}
            </div>
            <div className="row row-gap-2">
              {issues.counts.critical > 0 && <span className="badge badge-danger">{issues.counts.critical} critical</span>}
              {issues.counts.high > 0     && <span className="badge badge-warning">{issues.counts.high} high</span>}
              {issues.counts.medium > 0   && <span className="badge badge-info">{issues.counts.medium} medium</span>}
            </div>
          </div>
          <div className="stagger" style={{ display: 'grid', gap: 8 }}>
            {issues.issues.slice(0, 8).map((iss, idx) => (
              <div key={idx} className="flag-row" style={{ borderLeftColor: severityColor(iss.severity) }}>
                <span className={clsx('badge', SEVERITY_BADGE[iss.severity])} style={{ minWidth: 60, textAlign: 'center' }}>
                  {iss.severity}
                </span>
                <div className="col col-gap-2 flex-1">
                  <div style={{ color: 'var(--tx1)', fontWeight: 600, fontSize: 13 }}>{iss.title}</div>
                  <div className="text-tx3 text-xs">{iss.detail}</div>
                  {iss.actionable && <div className="text-tx3 text-xs">Action: <span style={{ color: 'var(--tx2)' }}>{iss.actionable}</span></div>}
                </div>
                <span className="text-tx3 text-xs" style={{ minWidth: 70, textAlign: 'right' }}>
                  {timeAgo(iss.created_at)}
                </span>
              </div>
            ))}
            {issues.issues.length > 8 && (
              <div className="text-tx3 text-xs" style={{ textAlign: 'center', padding: 'var(--s2)' }}>
                + {issues.issues.length - 8} more issues
              </div>
            )}
          </div>
        </div>
      )}

      {/* Active vendor detail */}
      {activeVendorRow && (
        <div className="card">
          <div className="row mb-3" style={{ justifyContent: 'space-between' }}>
            <div className="card-title" style={{ margin: 0 }}>{activeVendorRow.name} — detail</div>
            <div className="row row-gap-2">
              {['overview', 'transactions', 'sandbox'].map((tab) => {
                // Sandbox tab only shown for vendors that have a REST client
                // (YC + Paytrie today). Sumsub uses applicantId-based flows
                // that aren't useful in the refresh-button shape.
                if (tab === 'sandbox' && !['yellowcard', 'paytrie'].includes(activeVendor)) return null;
                const label = tab === 'overview' ? 'Overview' : tab === 'transactions' ? 'Transactions' : 'Sandbox Console';
                return (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setActiveTab(tab)}
                    className={clsx('btn', activeTab === tab ? 'btn-primary' : 'btn-ghost')}
                    style={{ padding: '6px 14px', fontSize: 12 }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          {activeTab === 'overview' && (
            <VendorOverview vendor={activeVendorRow} buckets={vendorBuckets} loading={vendorLoading} />
          )}
          {activeTab === 'transactions' && (
            <VendorTransactions vendor={activeVendorRow} txns={vendorTxns} loading={vendorLoading} />
          )}
          {activeTab === 'sandbox' && (
            <SandboxConsole
              vendor={activeVendor}
              sandbox={sandbox && sandbox.vendor === activeVendor ? sandbox : null}
              loading={sandboxLoading}
              error={sandboxError}
              onRefresh={async () => {
                setSandboxLoading(true);
                setSandboxError(null);
                try {
                  const r = await refreshVendorTransactions(activeVendor);
                  setSandbox(r);
                } catch (err) {
                  const msg = err?.response?.data?.error || err.message || 'Refresh failed';
                  setSandboxError(msg);
                } finally {
                  setSandboxLoading(false);
                }
              }}
            />
          )}
        </div>
      )}

      {summary && (
        <div className="text-tx3 text-xs mt-4" style={{ textAlign: 'center' }}>
          Last refreshed {timeAgo(summary.generated_at)} · auto-refresh every 30s
        </div>
      )}
    </>
  );
};

const VendorOverview = ({ vendor, buckets, loading }) => {
  const settledOk = vendor.transactions_24h.succeeded;
  const settledFail = vendor.transactions_24h.failed;
  const settleRate = (settledOk + settledFail) > 0 ? (settledOk / (settledOk + settledFail)) * 100 : 0;

  return (
    <div className="col col-gap-4">
      <div className="row row-gap-4" style={{ flexWrap: 'wrap' }}>
        <Stat label="Settle rate" value={`${settleRate.toFixed(1)}%`} accent={settleRate >= 95 ? 'var(--ok)' : settleRate >= 80 ? 'var(--warn)' : 'var(--err)'} />
        <Stat label="Avg latency" value={vendor.latency_ms.avg ? msPretty(vendor.latency_ms.avg) : '—'} />
        <Stat label="P95 latency" value={vendor.latency_ms.p95 ? msPretty(vendor.latency_ms.p95) : '—'} accent={vendor.latency_ms.p95 > vendor.sla_minutes * 60 * 1000 ? 'var(--err)' : 'var(--tx1)'} />
        <Stat label="SLA target" value={`${vendor.sla_minutes}m`} accent="var(--tx2)" />
        <Stat label="Conn errors 24h" value={vendor.events_24h.errors} accent={vendor.events_24h.errors > 0 ? 'var(--err)' : 'var(--tx1)'} />
      </div>

      <div className="row row-gap-4" style={{ flexWrap: 'wrap' }}>
        <Stat label="Last successful tx" value={timeAgo(vendor.last_successful_tx_at)} />
        <Stat label="Last failed tx" value={timeAgo(vendor.last_failed_tx_at)} accent={vendor.last_failed_tx_at ? 'var(--err)' : 'var(--tx1)'} />
        <Stat label="Last connectivity" value={`${vendor.last_event_ok ? '✓' : '✘'} ${timeAgo(vendor.last_event_at)}`} accent={vendor.last_event_ok ? 'var(--ok)' : 'var(--err)'} />
      </div>

      {loading ? (
        <div className="skeleton skeleton-block" style={{ height: 220 }} />
      ) : buckets.length === 0 ? (
        <div className="empty"><div className="empty-icon"><Icon name="dashboard" size={22} /></div><h3 style={{ fontSize: 14 }}>No data in last 24h</h3><p>Trend charts populate as transactions and webhook events flow.</p></div>
      ) : (
        <div className="row row-gap-4" style={{ flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 380px', minWidth: 320 }}>
            <div className="text-tx3 text-xs mb-2" style={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>
              Transaction count + failures (hourly)
            </div>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={buckets.map(formatBucket)}>
                <CartesianGrid stroke={chartPalette.grid} vertical={false} />
                <XAxis dataKey="hour" tick={{ fontSize: 10, fill: chartPalette.textMid }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: chartPalette.textMid }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip contentStyle={tooltipContentStyle} />
                <Bar dataKey="count"  fill={chartPalette.teal} radius={[4, 4, 0, 0]} />
                <Bar dataKey="failed" fill={chartPalette.err}  radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div style={{ flex: '1 1 380px', minWidth: 320 }}>
            <div className="text-tx3 text-xs mb-2" style={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>
              Avg settlement latency (hourly)
            </div>
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={buckets.map(formatBucket)}>
                <CartesianGrid stroke={chartPalette.grid} vertical={false} />
                <XAxis dataKey="hour" tick={{ fontSize: 10, fill: chartPalette.textMid }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: chartPalette.textMid }} axisLine={false} tickLine={false} tickFormatter={(v) => msPretty(v)} />
                <Tooltip contentStyle={tooltipContentStyle} formatter={(v) => msPretty(v)} />
                <Line type="monotone" dataKey="avg_latency_ms" stroke={chartPalette.teal} strokeWidth={2.4} dot={false} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
};

const VendorTransactions = ({ vendor, txns, loading }) => {
  if (loading && txns.data.length === 0) {
    return (
      <div className="col col-gap-3">
        {[0, 1, 2, 3].map((i) => <div key={i} className="skeleton skeleton-block" style={{ height: 48 }} />)}
      </div>
    );
  }
  if (txns.data.length === 0) {
    return (
      <div className="empty">
        <div className="empty-icon"><Icon name="clock" size={22} /></div>
        <h3 style={{ fontSize: 14 }}>No {txns.shape === 'integration_events' ? 'events' : 'transactions'} in last 24h</h3>
        <p>{vendor.configured ? 'Once webhooks fire, they will appear here.' : 'This vendor is not yet configured. Add API keys in Vercel env.'}</p>
      </div>
    );
  }

  if (txns.shape === 'integration_events') {
    return (
      <div className="table-wrap">
        <table>
          <thead><tr><th>Kind</th><th>OK?</th><th>HTTP</th><th>Message</th><th>Latency</th><th>Time</th></tr></thead>
          <tbody>
            {txns.data.map((e) => (
              <tr key={e.id}>
                <td>{e.kind}</td>
                <td><span className={clsx('badge', e.ok ? 'badge-success' : 'badge-danger')}>{e.ok ? 'ok' : 'err'}</span></td>
                <td>{e.status_code ?? '—'}</td>
                <td className="preview-cell" style={{ color: 'var(--tx2)', fontSize: 12, maxWidth: 320 }}>{e.message || '—'}</td>
                <td>{e.latency_ms != null ? `${e.latency_ms}ms` : '—'}</td>
                <td>{timeAgo(e.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>ID</th>
            <th>Status</th>
            <th>Corridor</th>
            <th style={{ textAlign: 'right' }}>Amount</th>
            <th>Settled in</th>
            <th>SLA</th>
            <th>Initiated</th>
          </tr>
        </thead>
        <tbody>
          {txns.data.map((t) => (
            <tr key={t.id}>
              <td style={{ color: 'var(--tx1)', fontWeight: 600, fontFamily: 'var(--mono)' }}>{t.id.slice(0, 8)}…</td>
              <td><span className={clsx('badge', statusBadgeFor(t.status))}>{t.status}</span></td>
              <td>{t.corridor || '—'}</td>
              <td style={{ textAlign: 'right' }}>{t.source_amount ? formatCurrency(t.source_amount, { compact: false, currency: t.source_currency || 'CAD' }) : '—'}</td>
              <td>{settlementDuration(t, vendor)}</td>
              <td>
                {t.sla_breached
                  ? <span className="badge badge-danger">Breached</span>
                  : <span className="text-tx3 text-xs">{vendor.sla_minutes}m</span>}
              </td>
              <td>{timeAgo(t.initiated_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const Stat = ({ label, value, accent }) => (
  <div className="col col-gap-2" style={{ minWidth: 120 }}>
    <div className="text-tx3 text-xs" style={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>{label}</div>
    <div style={{ fontSize: 18, fontWeight: 700, color: accent || 'var(--tx1)' }}>{value}</div>
  </div>
);

// ── Sandbox Console ────────────────────────────────────────────
// Lets Stanford manually pull recent transactions/orders from the
// provider's REST API (sandbox URL auto-selected outside production).
// Webhooks remain the primary signal; this is the backup view for when
// the provider portal shows a transaction the dashboard doesn't.
const SandboxConsole = ({ vendor, sandbox, loading, error, onRefresh }) => {
  const isYC = vendor === 'yellowcard';
  return (
    <div>
      <div className="row mb-3" style={{ alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div className="text-tx3 text-xs" style={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>
            {isYC ? 'Yellow Card' : 'Paytrie'} — Sandbox Console
          </div>
          <div className="text-tx2 text-sm" style={{ marginTop: 4 }}>
            Pull the most-recent {isYC ? 'transactions' : 'orders'} from{' '}
            <span className="mono" style={{ wordBreak: 'break-all' }}>
              {sandbox?.base_url || (isYC ? 'sandbox.api.yellowcard.io' : 'sandbox-api.paytrie.com')}
            </span>
            . The webhook into <code>/api/webhooks/{vendor}</code> is still the primary feed;
            use this when a webhook missed or while testing a new corridor flow.
          </div>
        </div>
        <button type="button" className="btn btn-primary" onClick={onRefresh} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh from provider'}
        </button>
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: 12 }}>{error}</div>}

      {sandbox && (
        <div className="row row-gap-4" style={{ marginBottom: 12, flexWrap: 'wrap' }}>
          <Stat label="Items"       value={sandbox.count ?? (sandbox.items?.length || 0)} />
          <Stat label="Latency"     value={msPretty(sandbox.latency_ms)} />
          <Stat label="Environment" value={sandbox.environment || (process.env.NODE_ENV === 'production' ? 'production' : 'sandbox')} />
          <Stat label="Fetched"     value={timeAgo(sandbox.fetched_at)} />
        </div>
      )}

      {!sandbox && !loading && !error && (
        <div className="text-tx3 text-sm">No data yet — click <strong>Refresh from provider</strong> to fetch.</div>
      )}

      {sandbox && Array.isArray(sandbox.items) && sandbox.items.length > 0 && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table className="table table-compact" style={{ margin: 0 }}>
            <thead>
              <tr>
                <th>ID</th>
                <th>Status</th>
                <th>Amount</th>
                <th>Currency</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {sandbox.items.slice(0, 25).map((it, idx) => {
                const id        = it.id || it.transactionId || it.orderId || it._id || `(row-${idx})`;
                const status    = it.status || it.state || '—';
                const amount    = it.amount ?? it.value ?? it.cryptoAmount ?? '—';
                const currency  = it.currency || it.dest_currency || it.cryptoCurrency || '—';
                const updatedAt = it.updatedAt || it.updated_at || it.createdAt || it.created_at;
                return (
                  <tr key={`${id}-${idx}`}>
                    <td className="mono" style={{ wordBreak: 'break-all', fontSize: 12 }}>{String(id).slice(0, 32)}</td>
                    <td><span className={clsx('badge', statusBadgeFor(status))}>{status}</span></td>
                    <td>{typeof amount === 'number' ? amount.toLocaleString() : amount}</td>
                    <td>{currency}</td>
                    <td>{updatedAt ? timeAgo(updatedAt) : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {sandbox && (!sandbox.items || sandbox.items.length === 0) && (
        <div className="text-tx3 text-sm">Provider returned 0 items. Trigger a test transaction in the {isYC ? 'YC' : 'Paytrie'} portal then refresh again.</div>
      )}
    </div>
  );
};

function severityColor(s) {
  return s === 'critical' ? 'var(--err)'
    : s === 'high'        ? 'var(--warn)'
    : s === 'medium'      ? 'var(--teal)'
    :                       'var(--tx3)';
}
function msPretty(ms) {
  if (ms == null) return '—';
  if (ms < 1000)  return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60_000).toFixed(1)}m`;
}
function statusBadgeFor(s) {
  if (s === 'failed') return 'badge-danger';
  if (s === 'usdc_settled' || s === 'offramp_confirmed') return 'badge-success';
  if (s === 'onramp_confirmed' || s === 'offramp_initiated') return 'badge-warning';
  return 'badge-muted';
}
function settlementDuration(t, vendor) {
  const settleCol = vendor.role === 'on_ramp' ? 'usdc_settled_at' : 'offramp_confirmed_at';
  if (!t[settleCol] || !t.initiated_at) return t.status === 'failed' ? '—' : 'pending';
  const ms = new Date(t[settleCol]).getTime() - new Date(t.initiated_at).getTime();
  return msPretty(ms);
}
function formatBucket(b) {
  return {
    ...b,
    hour: new Date(b.bucket).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
  };
}

export default Vendors;
